// src/components/AutomacaoIA.js — Aba "IA Assistida" da página /automacao.
//
// UI do módulo BYOK de IA (Bring-Your-Own-Key: cliente conecta a chave
// dele com o provider, custo direto lá — coerente com o discurso "Cetto
// orquestra, provider cobra direto" já usado pra template Meta).
//
// Nasceu como mock (ago/26) e hoje está toda ligada no backend real:
// GET/PUT /ai/config, POST/DELETE /ai/config/key e
// GET /ai/suggest-reply/stats.
//
// Provider: só Anthropic Claude ativo. OpenAI + Gemini aparecem como
// "Em breve" (desabilitados) — o backend rejeita os dois.
//
// ─── Dois modos de uso da IA, e o toggle controla só UM deles ──────────
//
//   ASSISTIDO — o botão "Sugerir com IA" no ChatPage. Agente pede, lê e
//     decide. Vale sempre que houver chave válida, com o toggle ligado
//     ou desligado.
//   AUTÔNOMO — o toggle "Ativar IA no chat" (`ai_enabled`). A IA responde
//     sozinha as mensagens que chegam no WhatsApp, depois da janela de
//     debounce configurada no backend.
//
// Confundir os dois é o erro fácil aqui: `enabled` do backend NÃO é o
// toggle, é "tem chave válida conectada". O toggle é `ai_enabled`.
//
// Estados que a UI cobre:
//   - Sem chave salva → input + validar
//   - Validando (loading)
//   - Chave inválida (erro banner vermelho)
//   - Chave válida salva (banner verde + máscara + substituir)
//   - Toggle do piloto automático, só clicável depois de chave válida,
//     persistido na hora (sem depender do "Salvar diretrizes")
//
// Signature visual: o textarea de "Restrições Obrigatórias" fica dentro de
// um wrapper amber com badge "NUNCA RESPONDA" — enquadra as restrições
// como regra dura (não sugestão), comunicando engenharia madura. Segundo
// signature: o KPI "Provedor Ativo" quebra o padrão dos outros 3 (mostra
// logo+nome em vez de número), o que sinaliza que essa métrica é sobre
// identidade de conexão, não volume.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem, Select,
  CircularProgress, Chip, Switch, IconButton, Tooltip, Menu, Dialog,
  ButtonBase, useMediaQuery, useTheme,
} from '@mui/material';
import {
  FiCpu, FiTarget, FiKey, FiEye, FiEyeOff, FiCheck, FiAlertCircle,
  FiLock, FiRefreshCw, FiSlash, FiZap, FiClock, FiBarChart2,
  FiPlus, FiTrash2, FiChevronLeft, FiChevronRight, FiMinus, FiX,
  FiFileText, FiImage, FiUpload, FiPaperclip,
} from 'react-icons/fi';
import api from '../services/Api';
import { C } from './automacao/tema';

const SYNE = "'Syne', system-ui, sans-serif";
const OUTFIT = "'Outfit', system-ui, sans-serif";

// Converte err.response.data.detail em string legível SEMPRE. Pydantic v2
// (FastAPI 0.100+) devolve validation errors como LISTA de objetos
// `[{type, loc, msg, input, ctx}]`. Passar isso pro toast/JSX explode com
// React error #31 ("Objects are not valid as a React child") e a UI vira
// tela branca — foi exatamente o que aconteceu com o `about_company`
// passando de max_length. Aqui a gente colapsa qualquer formato num
// texto único antes de exibir.
function parseErrorDetail(err, fallback = 'Erro ao processar a requisição.') {
  const raw = err?.response?.data?.detail;
  if (!raw) return fallback;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .map(e => {
        if (typeof e === 'string') return e;
        const loc = Array.isArray(e?.loc) ? e.loc.filter(x => x !== 'body').join('.') : '';
        const msg = e?.msg || 'inválido';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(' · ');
  }
  if (typeof raw === 'object') return raw.msg || JSON.stringify(raw);
  return String(raw);
}

// Cores: components/automacao/tema.js (tokens do chat, claro e escuro).

// ─── Providers config ──────────────────────────────────────────────────────
// Cada card renderiza a partir daqui. `available: false` desativa o card e
// mostra badge "Em breve" — dia 1 só Claude tá ligado. IDs em UPPERCASE
// pra bater direto com o enum AIProvider do backend (CLAUDE/OPENAI/GEMINI)
// — evita camada de mapping. `defaultModel` só aparece antes de haver
// snapshot real (nunca conectou / sem stats ainda); depois do primeiro
// suggest-reply, o backend devolve o pin exato tipo
// "claude-3-5-sonnet-20241022" e a UI exibe isso.
const PROVIDERS = [
  {
    id: 'CLAUDE',
    name: 'Claude',
    vendor: 'Anthropic',
    defaultModel: 'claude-3-5-sonnet',
    logoText: 'A',
    logoBg: '#191a1b',
    logoColor: '#CC785C',
    tag: '★ Recomendado — melhor escrita PT-BR',
    tagBg: 'greenBg',
    placeholder: 'sk-ant-api03-…',
    available: true,
  },
  {
    id: 'OPENAI',
    name: 'ChatGPT',
    vendor: 'OpenAI',
    defaultModel: 'gpt-4o-mini',
    logoText: '⊹',
    logoBg: '#000',
    logoColor: '#fff',
    tag: '⚡ Rápido e baixo custo',
    tagBg: 'blueBg',
    placeholder: 'sk-…',
    available: false,
  },
  {
    id: 'GEMINI',
    name: 'Gemini',
    vendor: 'Google',
    defaultModel: 'gemini-1.5-pro',
    logoText: '✦',
    logoBg: 'linear-gradient(135deg,#4285F4 0%,#34A853 40%,#FBBC05 70%,#EA4335 100%)',
    logoColor: '#fff',
    tag: '∞ Ideal para históricos longos',
    tagBg: 'purpBg',
    placeholder: 'AIzaSy…',
    available: false,
  },
];

// Tokens de tags de provider — resolve pela chave em PROVIDERS acima
// pra evitar poluir esse config com objetos de style.
const TAG_STYLE = {
  greenBg: { bg: C.greenBg, color: C.greenD, border: C.greenB },
  blueBg:  { bg: C.blueBg,  color: C.blueD,  border: C.blueB  },
  purpBg:  { bg: C.purpBg,  color: C.purple, border: C.purpB  },
};

// ─── Sub-componentes visuais ───────────────────────────────────────────────

// Header de card grande (ícone quadrado colorido + título + sub + slot direito).
function SectionHead({ icon, iconBg, iconBorder, iconColor, title, sub, right }) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      spacing={1.5}
      sx={{
        p: '16px 20px', borderBottom: `1px solid ${C.bdr}`,
        bgcolor: C.surf,
      }}
    >
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box sx={{
          width: 38, height: 38, borderRadius: '9px', flexShrink: 0,
          bgcolor: iconBg, border: `1px solid ${iconBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: iconColor,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{
            fontFamily: SYNE, fontWeight: 700, fontSize: '0.9rem', color: C.text, mb: 0.3,
          }}>
            {title}
          </Typography>
          <Typography sx={{
            fontFamily: SYNE, fontSize: '0.76rem', color: C.text2,
            maxWidth: 560, lineHeight: 1.55,
          }}>
            {sub}
          </Typography>
        </Box>
      </Stack>
      {right}
    </Stack>
  );
}

// Card KPI genérico com faixa gradient no topo (accent varia por métrica).
function KpiCard({ label, gradient, children }) {
  return (
    <Box sx={{
      position: 'relative', bgcolor: C.card, border: `1px solid ${C.bdr}`,
      borderRadius: '10px', p: '18px 18px 14px',
      boxShadow: C.sombra, overflow: 'hidden',
    }}>
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: gradient,
      }} />
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: C.text3, mb: 1,
      }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// Radio card de provider (Claude / GPT / Gemini). Estado selecionado
// pinta em verde; estado indisponível fica opaco + badge "Em breve".
function ProviderCard({ p, selected, onSelect, activeModel }) {
  const disabled = !p.available;
  const tagStyle = TAG_STYLE[p.tagBg] || TAG_STYLE.greenBg;
  // Se este provider é o ativo E o backend já sabe o snapshot exato do
  // modelo em uso (após ping ou primeira sugestão real), mostra ele em
  // vez do defaultModel — fica claro pro cliente qual pin está rodando
  // ("claude-3-5-sonnet-20241022" > "claude-3-5-sonnet").
  const modelToShow = (selected && activeModel) || p.defaultModel;

  return (
    <Box
      onClick={() => { if (!disabled) onSelect(p.id); }}
      sx={{
        position: 'relative', bgcolor: selected ? C.greenBg : C.surf,
        border: `2px solid ${selected ? C.green : C.bdr}`,
        borderRadius: '10px', p: 2, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color .18s, background .18s, box-shadow .18s',
        boxShadow: selected ? `0 0 0 1px ${C.greenB}, 0 4px 16px ${C.greenBg}` : 'none',
        '&:hover': disabled ? {} : { borderColor: selected ? C.green : C.bdr2, bgcolor: selected ? C.greenBg : C.card },
      }}
    >
      {/* Radio circle */}
      <Box sx={{
        position: 'absolute', top: 14, right: 14,
        width: 18, height: 18, borderRadius: '50%',
        border: `2px solid ${selected ? C.green : C.bdr2}`,
        bgcolor: selected ? C.green : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.sobreCor }} />}
      </Box>

      {/* Logo quadrado com letra do provider */}
      <Box sx={{
        width: 38, height: 38, borderRadius: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 1.2, fontFamily: OUTFIT, fontWeight: 900, fontSize: '1.1rem',
        background: p.logoBg, color: p.logoColor,
        border: '1px solid #333',
      }}>
        {p.logoText}
      </Box>

      <Typography sx={{ fontFamily: SYNE, fontSize: '0.82rem', fontWeight: 700, color: C.text, mb: 0.3 }}>
        {p.name}
      </Typography>
      <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.66rem', color: C.text3, mb: 1.1 }}>
        {p.vendor} · {modelToShow}
      </Typography>

      {disabled ? (
        <Chip
          label="Em breve"
          size="small"
          sx={{
            height: 20, borderRadius: '20px',
            bgcolor: C.amberBg, color: C.amberD, border: `1px solid ${C.amberB}`,
            fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ) : (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
          padding: '2px 8px', borderRadius: '20px',
          bgcolor: tagStyle.bg, color: tagStyle.color, border: `1px solid ${tagStyle.border}`,
          letterSpacing: '0.04em',
        }}>
          {p.tag}
        </Box>
      )}
    </Box>
  );
}

// Label uppercase pequenininho pra campos de form.
function TuneLabel({ children, hint }) {
  return (
    <Typography sx={{
      fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
      letterSpacing: '0.12em', textTransform: 'uppercase',
      color: C.text3, mb: 0.6,
    }}>
      {children}
      {hint && (
        <Box component="span" sx={{
          ml: 0.5, fontFamily: SYNE, fontSize: '0.62rem', fontWeight: 400,
          letterSpacing: 0, textTransform: 'none', color: C.text3,
        }}>
          — {hint}
        </Box>
      )}
    </Typography>
  );
}

// ─── Contexto do copiloto em blocos ────────────────────────────────────────
//
// Out/26. Antes era UM textarea de 20.000 caracteres chamado "Sobre a
// empresa", onde o cliente empilhava descrição, comportamento desejado e
// quebra de objeção no mesmo bloco. Virava um muro de texto que ninguém
// relia e sobre o qual ninguém conseguia raciocinar.
//
// O cliente descreveu o que queria como "páginas", e o desenho leva isso a
// sério: cada bloco é uma folha com cabeçalho próprio. A NUMERAÇÃO não é
// enfeite — é a ordem em que o modelo lê o contexto, e mover uma folha
// muda o prompt de verdade.
//
// No começo tudo nascia expandido ("isso tudo fica visível para ele"). Com
// 10 páginas abertas a aba virou um muro de novo (out/26, redesign do
// dono): agora cada página é um card — título, as primeiras linhas, o
// tamanho e quanto pesa no contexto — e o texto inteiro abre numa janela.
// O que importava continua à vista: a ordem, o que está ligado e o custo.

// Modelos oferecidos ao criar uma folha nova. São os casos que o próprio
// cliente citou — descrição, comportamento, objeções —, pra ele não
// começar de uma folha em branco sem saber o que escrever.
const MODELOS_DE_BLOCO = [
  {
    titulo: 'Sobre a empresa',
    placeholder: 'O que vocês vendem, para quem, o que faz vocês diferentes dos concorrentes, horário de atendimento, região que atendem…',
  },
  {
    titulo: 'Comportamento',
    placeholder: 'Como a IA deve se portar: tamanho das respostas, se pode chamar pelo primeiro nome, quando perguntar antes de responder, como encerrar…',
  },
  {
    titulo: 'Quebra de objeções',
    placeholder: 'As dúvidas que mais aparecem e a melhor resposta para cada uma. Ex.: "está caro" → explique o que está incluso antes de falar de desconto.',
  },
  {
    titulo: 'Perguntas frequentes',
    placeholder: 'Pergunta e resposta, uma por linha. Quanto mais específico, menos a IA precisa adivinhar.',
  },
  { titulo: 'Em branco', placeholder: '' },
];

// Medidor do orçamento de contexto.
//
// É o elemento que justifica a reforma inteira: o cliente passa a VER que
// tudo que ele escreve aqui vai pra IA em toda mensagem e é cobrado na
// chave dele. Antes, o limite só aparecia como um 422 na hora de salvar.
function MedidorDeContexto({ usado, limite, maxBlocos, blocosUsados }) {
  const pct = limite > 0 ? Math.min(100, (usado / limite) * 100) : 0;
  const estourou = usado > limite;
  const apertado = pct >= 80;
  const cor = estourou ? C.red : apertado ? C.amber : C.green;
  // Mesma divisão que o backend usa (ai_contexto.CARACTERES_POR_TOKEN).
  const tokens = Math.floor(usado / 4);

  return (
    <Box sx={{
      border: `1px solid ${estourou ? C.redB : C.bdr}`,
      bgcolor: estourou ? C.redBg : C.surf,
      borderRadius: '8px', p: 1.6, mb: 2,
    }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.9 }}>
        <Typography sx={{
          fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: C.text3,
        }}>
          Contexto do copiloto
        </Typography>
        <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.72rem', fontWeight: 700, color: cor }}>
          {usado.toLocaleString('pt-BR')}
          <Box component="span" sx={{ color: C.text3, fontWeight: 400 }}>
            {' / '}{limite.toLocaleString('pt-BR')} caracteres
          </Box>
        </Typography>
      </Stack>
      <Box sx={{ height: 5, bgcolor: C.inset, borderRadius: '3px', overflow: 'hidden' }}>
        <Box sx={{
          width: `${pct}%`, height: '100%', bgcolor: cor,
          borderRadius: '3px', transition: 'width .4s ease, background-color .3s ease',
        }} />
      </Box>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.64rem', color: estourou ? C.redD : C.text2,
        mt: 0.9, lineHeight: 1.5,
      }}>
        {estourou ? (
          <>Passou do limite. Desligue um bloco ou encurte o texto para conseguir salvar.</>
        ) : (
          <>
            ~{tokens.toLocaleString('pt-BR')} tokens enviados à IA <strong>em cada mensagem</strong>,
            cobrados na sua chave. {blocosUsados} de {maxBlocos} blocos.
          </>
        )}
      </Typography>
    </Box>
  );
}

// Interruptor verde dos cards e da janela.
const switchVerdeSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: C.green },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.green },
};

const doisDigitos = (n) => String(n).padStart(2, '0');

// Quanto uma página pesa no contexto, em %, sobre o total que vai pra IA
// (páginas ligadas + restrições). Desligada não pesa.
const fatiaDoContexto = (bloco, usado) => (
  usado > 0 ? Math.round(((bloco.content || '').trim().length / usado) * 100) : 0
);

// Uma página de contexto, fechada: o card da grade.
//
// O card inteiro abre a página, mas o interruptor é outro controle: os dois
// são irmãos (o botão "esticado" cobre o card por um ::after e o
// interruptor fica por cima), e não um dentro do outro — botão dentro de
// botão some do leitor de tela e o clique no interruptor abriria a janela.
function CartaoDePagina({
  bloco, indice, usado, alterada, arrastando, alvo,
  onAbrir, onLigar, onArrastar, onSobre, onSoltar, onFimDoArraste,
}) {
  const ligada = !!bloco.active;
  const titulo = (bloco.title || '').trim();
  const chars = (bloco.content || '').trim().length;
  const previa = (bloco.content || '').replace(/\s*\n+\s*/g, ' ').trim();
  const fatia = fatiaDoContexto(bloco, usado);

  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(indice)); } catch { /* Safari antigo */ }
        onArrastar(indice);
      }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onSobre(indice); }}
      onDrop={(e) => { e.preventDefault(); onSoltar(indice); }}
      onDragEnd={onFimDoArraste}
      sx={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        border: `1px solid ${ligada ? C.bdr2 : C.bdr}`, borderRadius: '8px',
        bgcolor: ligada ? C.card : C.surf,
        opacity: arrastando ? 0.4 : (ligada ? 1 : 0.62),
        outline: alvo ? `2px dashed ${C.green}` : '2px solid transparent', outlineOffset: '2px',
        transition: 'border-color .15s, box-shadow .15s, transform .15s, opacity .2s',
        '&:hover': { borderColor: C.greenB, boxShadow: C.sombra, transform: 'translateY(-1px)' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{
        pl: 1.5, pr: 0.5, py: 0.4, bgcolor: ligada ? C.surf : C.inset,
        borderBottom: `1px solid ${C.bdr}`, borderRadius: '8px 8px 0 0',
      }}>
        {/* A numeração é a ordem em que o modelo lê — por isso ela existe. */}
        <Typography sx={{
          fontFamily: OUTFIT, fontSize: '0.7rem', fontWeight: 700,
          color: ligada ? C.green : C.text3, minWidth: 20,
        }}>
          {doisDigitos(indice + 1)}
        </Typography>

        {/* role="button" num <span>, não <button>: o Firefox não deixa
            arrastar a partir de um <button>, e o ::after dele cobre o card. */}
        <Box
          component="span" role="button" tabIndex={0} onClick={onAbrir}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(); }
          }}
          aria-label={`Abrir página ${indice + 1}: ${titulo || 'sem nome'}`}
          sx={{
            flex: 1, minWidth: 0, p: 0, border: 0, background: 'none', cursor: 'pointer',
            textAlign: 'left', fontFamily: SYNE, fontSize: '0.78rem', fontWeight: 700,
            color: titulo ? C.text : C.text3, lineHeight: 1.5,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            // O ::after estica o botão sobre o card inteiro.
            '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: '8px' },
            '&:focus-visible': { outline: 'none' },
            '&:focus-visible::after': { boxShadow: `0 0 0 2px ${C.green}, 0 0 0 5px ${C.greenBg}` },
          }}
        >
          {titulo || 'Sem nome'}
        </Box>

        {alterada && (
          <Tooltip title="Alterada — salve as diretrizes">
            <Box component="span" aria-label="Alterada, ainda não salva" role="img" sx={{
              width: 6, height: 6, borderRadius: '50%', bgcolor: C.amber, flexShrink: 0,
              position: 'relative', zIndex: 1,
            }} />
          </Tooltip>
        )}

        <Tooltip title={ligada
          ? 'Ligada: vai para a IA em toda mensagem'
          : 'Desligada: fica salva, mas não vai para a IA'}>
          <Switch
            size="small" checked={ligada} onChange={(e) => onLigar(e.target.checked)}
            slotProps={{ input: { role: 'switch', 'aria-label': `${ligada ? 'Desligar' : 'Ligar'} ${titulo || `página ${indice + 1}`}` } }}
            sx={{ ...switchVerdeSx, position: 'relative', zIndex: 1 }}
          />
        </Tooltip>
      </Stack>

      <Typography sx={{
        px: 1.5, pt: 1.2, flex: 1,
        fontFamily: SYNE, fontSize: '0.72rem', lineHeight: 1.55,
        color: previa ? C.text2 : C.text3, fontStyle: previa ? 'normal' : 'italic',
        // 4 linhas fixas: os cards da mesma fileira ficam da mesma altura.
        height: 'calc(0.72rem * 1.55 * 4)', boxSizing: 'content-box',
        display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {previa || 'Página vazia. Clique para escrever.'}
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 1.5, pt: 1.1, pb: 1.2 }}>
        <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.64rem', color: C.text3 }}>
          {chars.toLocaleString('pt-BR')} caracteres
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          {ligada && (
            <Box sx={{ width: 36, height: 3, borderRadius: 2, bgcolor: C.inset, overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.max(fatia, 2)}%`, height: '100%', bgcolor: C.green, borderRadius: 2 }} />
            </Box>
          )}
          <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.62rem', color: C.text3, whiteSpace: 'nowrap' }}>
            {ligada ? `${fatia}% do contexto` : 'Desligada'}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

// Uma página de contexto, aberta: a janela onde o texto inteiro é editado.
//
// Fica na ordem da grade — "Anterior"/"Próxima" passam de uma página pra
// outra sem fechar, e as setas do cabeçalho movem a página (é o jeito de
// reordenar pelo teclado e no celular, onde não há arrastar). Nada é salvo
// aqui: as mudanças vão pra lista e entram na IA no "Salvar diretrizes",
// como antes.
function JanelaDaPagina({
  chave, bloco, indice, total, usado, placeholder, focarTitulo, telaCheia,
  onMudar, onMover, onIr, onRemover, onFechar,
}) {
  const [confirmando, setConfirmando] = useState(false);
  const tituloRef = useRef(null);
  const corpoRef = useRef(null);

  // Abriu ou trocou de página: o cursor vai pro começo do texto (ou pro
  // título, numa página nova em branco) e a confirmação de remover some.
  // Pela `chave`, não pelo índice: mover a página com as setas muda o
  // índice, e o foco não pode sair do botão que a pessoa está apertando.
  useEffect(() => {
    setConfirmando(false);
    const t = setTimeout(() => {
      const el = focarTitulo ? tituloRef.current : corpoRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      if (!focarTitulo) { el.setSelectionRange(0, 0); el.scrollTop = 0; }
    }, 0);
    return () => clearTimeout(t);
  }, [chave, focarTitulo]);

  const ligada = !!bloco.active;
  const titulo = (bloco.title || '').trim();
  const chars = (bloco.content || '').length;
  const tituloId = `pagina-contexto-${indice}`;

  const botaoNeutroSx = {
    fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 600, textTransform: 'none',
    color: C.text2, bgcolor: C.card, border: `1px solid ${C.bdr2}`, borderRadius: '6px',
    px: 1.2, py: 0.4, minWidth: 0,
    '&:hover': { bgcolor: C.inset },
    '&.Mui-disabled': { color: C.text3, opacity: 0.5 },
  };

  return (
    <Dialog
      open
      onClose={(_, motivo) => {
        // Esc com a confirmação aberta só desiste de remover.
        if (motivo === 'escapeKeyDown' && confirmando) { setConfirmando(false); return; }
        onFechar();
      }}
      fullScreen={telaCheia}
      maxWidth={false}
      aria-labelledby={tituloId}
      PaperProps={{
        sx: {
          width: telaCheia ? '100%' : 'min(780px, calc(100% - 32px))',
          maxHeight: telaCheia ? '100%' : 'calc(100vh - 64px)',
          m: telaCheia ? 0 : 2, borderRadius: telaCheia ? 0 : '12px',
          bgcolor: C.card, backgroundImage: 'none',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        },
      }}
    >
      {/* Cabeçalho: ordem, nome e posição na fila de leitura */}
      <Stack direction="row" alignItems="center" spacing={1.4} sx={{
        pl: 2.5, pr: 1.5, py: 1.6, bgcolor: C.surf, borderBottom: `1px solid ${C.bdr}`,
      }}>
        <Typography sx={{
          fontFamily: OUTFIT, fontSize: '0.82rem', fontWeight: 700,
          color: ligada ? C.green : C.text3, bgcolor: C.card,
          border: `1px solid ${C.bdr}`, borderRadius: '6px', px: 1, py: 0.3, lineHeight: 1.5,
        }}>
          {doisDigitos(indice + 1)}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="input" ref={tituloRef} id={tituloId}
            value={bloco.title}
            onChange={e => onMudar({ ...bloco, title: e.target.value })}
            maxLength={80}
            placeholder="Nome desta página"
            aria-label="Nome da página"
            sx={{
              width: '100%', boxSizing: 'border-box', p: '2px 0',
              fontFamily: SYNE, fontSize: '1rem', fontWeight: 700, color: C.text,
              bgcolor: 'transparent', border: 'none', borderBottom: '1px solid transparent', outline: 'none',
              '&::placeholder': { color: C.text3, opacity: 1 },
              '&:hover': { borderBottomColor: C.bdr2 },
              '&:focus': { borderBottomColor: C.green },
            }}
          />
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.66rem', color: C.text3, mt: '1px' }}>
            Página {indice + 1} de {total} · a IA lê esta página em {indice + 1}º lugar
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.2}>
          <Tooltip title="Mover para antes">
            <span>
              <IconButton size="small" aria-label="Mover para antes" disabled={indice === 0}
                onClick={() => onMover(indice, indice - 1)} sx={{ color: C.text3, '&:hover': { color: C.text2 } }}>
                <FiChevronLeft size={15} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Mover para depois">
            <span>
              <IconButton size="small" aria-label="Mover para depois" disabled={indice === total - 1}
                onClick={() => onMover(indice, indice + 1)} sx={{ color: C.text3, '&:hover': { color: C.text2 } }}>
                <FiChevronRight size={15} />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ width: '1px', height: 20, bgcolor: C.bdr, mx: 0.8 }} />
          <IconButton size="small" aria-label="Fechar" onClick={onFechar} sx={{ color: C.text2 }}>
            <FiX size={18} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Liga/desliga e remover */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{
        pl: 1.5, pr: 1.8, py: 0.8, borderBottom: `1px solid ${C.bdr}`,
      }}>
        <Stack direction="row" alignItems="center" spacing={0.4}>
          <Switch
            size="small" checked={ligada}
            onChange={e => onMudar({ ...bloco, active: e.target.checked })}
            slotProps={{ input: { role: 'switch', 'aria-label': ligada ? 'Desligar esta página' : 'Ligar esta página' } }}
            sx={switchVerdeSx}
          />
          {/* No celular fica só "Ligada"/"Desligada": a frase inteira
              empurrava o "Remover página" pra linha de baixo. */}
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.72rem', color: ligada ? C.text2 : C.text3 }}>
            {ligada ? 'Ligada' : 'Desligada'}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {ligada ? ' — vai para a IA em toda mensagem' : ' — fica salva, mas não vai para a IA'}
            </Box>
          </Typography>
        </Stack>
        <Button
          size="small" startIcon={<FiTrash2 size={12} />} onClick={() => setConfirmando(true)}
          sx={{
            fontFamily: SYNE, fontSize: '0.7rem', fontWeight: 600, textTransform: 'none',
            color: C.text2, borderRadius: '6px', px: 1,
            '&:hover': { color: C.red, bgcolor: C.redBg },
          }}
        >
          Remover página
        </Button>
      </Stack>

      <Box
        component="textarea" ref={corpoRef}
        value={bloco.content}
        onChange={e => onMudar({ ...bloco, content: e.target.value })}
        maxLength={20000}
        placeholder={placeholder}
        aria-label={`Texto da página ${titulo || indice + 1}`}
        sx={{
          flex: 1, minHeight: 340, width: '100%', boxSizing: 'border-box', resize: 'none',
          border: 'none', outline: 'none', px: 2.5, py: 2.2,
          fontFamily: SYNE, fontSize: '0.82rem', lineHeight: 1.65, color: C.text, bgcolor: C.card,
          '&::placeholder': { color: C.text3, opacity: 1 },
        }}
      />

      {confirmando ? (
        <Stack direction="row" alignItems="center" spacing={1.4} sx={{
          pl: 2.5, pr: 1.8, py: 1.5, borderTop: `1px solid ${C.redB}`, bgcolor: C.redBg, flexWrap: 'wrap', rowGap: 1,
        }}>
          <FiAlertCircle size={14} color={C.red} style={{ flexShrink: 0 }} />
          <Typography role="alert" sx={{ flex: 1, minWidth: 200, fontFamily: SYNE, fontSize: '0.74rem', color: C.redD, lineHeight: 1.5 }}>
            Remover <b>{titulo ? `“${titulo}”` : `a página ${indice + 1}`}</b>? O texto desta página sai do contexto da IA.
          </Typography>
          <Button autoFocus onClick={() => setConfirmando(false)} sx={botaoNeutroSx}>Cancelar</Button>
          <Button
            startIcon={<FiTrash2 size={12} />} onClick={() => onRemover(indice)}
            sx={{
              fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none',
              bgcolor: C.red, color: C.sobreCor, borderRadius: '6px', px: 1.8, py: 0.5,
              '&:hover': { bgcolor: C.redHover },
            }}
          >
            Remover página
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{
          pl: 2.5, pr: 1.8, py: 1.5, borderTop: `1px solid ${C.bdr}`, bgcolor: C.surf, flexWrap: 'wrap', rowGap: 1.2,
        }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.72rem', fontWeight: 700, color: C.text2 }}>
              {chars.toLocaleString('pt-BR')}
              <Box component="span" sx={{ fontWeight: 400, color: C.text3 }}>
                {' / 20.000 caracteres · ~'}{Math.floor(chars / 4).toLocaleString('pt-BR')} tokens · {ligada ? `${fatiaDoContexto(bloco, usado)}% do contexto` : 'fora do contexto'}
              </Box>
            </Typography>
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.64rem', color: C.text3 }}>
              As alterações entram na IA quando você salvar as diretrizes.
            </Typography>
          </Box>
          {/* No celular os três botões dividem a largura, ao alcance do polegar. */}
          <Stack direction="row" alignItems="center" spacing={0.8} sx={{
            width: { xs: '100%', sm: 'auto' }, '& > button': { flex: { xs: 1, sm: 'none' } },
          }}>
            <Button onClick={() => onIr(indice - 1)} disabled={indice === 0}
              startIcon={<FiChevronLeft size={12} />} sx={botaoNeutroSx}>
              Anterior
            </Button>
            <Button onClick={() => onIr(indice + 1)} disabled={indice === total - 1}
              endIcon={<FiChevronRight size={12} />} sx={botaoNeutroSx}>
              Próxima
            </Button>
            <Button
              onClick={onFechar} startIcon={<FiCheck size={12} />}
              sx={{
                fontFamily: SYNE, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none',
                bgcolor: C.green, color: C.sobreCor, borderRadius: '6px', px: 2.2, py: 0.5, ml: { xs: 0, sm: 0.5 },
                boxShadow: `0 2px 6px ${C.greenGlow}`,
                '&:hover': { bgcolor: C.greenHover },
              }}
            >
              Concluir
            </Button>
          </Stack>
        </Stack>
      )}
    </Dialog>
  );
}

// ─── Biblioteca de arquivos que a IA pode enviar ───────────────────────────
//
// Out/26. Outra coisa que as páginas de contexto: contexto é o que a IA
// SABE, isto é o que ela PODE MANDAR. O mesmo cardápio costuma ser os dois,
// por caminhos diferentes — o texto vira página de contexto, o arquivo fica
// aqui.
//
// O `slug` aparece na tela de propósito. É o nome que a IA usa internamente
// pra pedir o envio, e mostrá-lo torna o mecanismo visível em vez de mágico:
// o cliente entende por que a descrição importa e por que o nome do arquivo
// não muda sozinho.
function ArquivoDaIA({ arquivo, onMudar, onRemover, salvando }) {
  const desligado = !arquivo.active;
  const ehImagem = arquivo.kind === 'IMAGE';

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.2} sx={{
      border: `1px solid ${C.bdr}`, borderRadius: '8px',
      p: 1.2, mb: 1, bgcolor: desligado ? C.surf : C.card,
      opacity: desligado ? 0.62 : 1, transition: 'opacity .2s',
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '6px', flexShrink: 0,
        bgcolor: ehImagem ? C.purpBg : C.blueBg,
        border: `1px solid ${ehImagem ? C.purpB : C.blueB}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: ehImagem ? C.purple : C.blue, mt: 0.2,
      }}>
        {ehImagem ? <FiImage size={14} /> : <FiFileText size={14} />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.3 }}>
          <TextField
            variant="standard"
            value={arquivo.title}
            onChange={e => onMudar({ ...arquivo, title: e.target.value })}
            inputProps={{ maxLength: 80 }}
            sx={{
              flex: 1, minWidth: 0,
              '& .MuiInput-input': {
                fontFamily: SYNE, fontSize: '0.78rem', fontWeight: 700,
                color: C.text, padding: '1px 0',
              },
              '& .MuiInput-underline:before': { borderBottom: 'none' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottom: `1px solid ${C.bdr2}`,
              },
            }}
          />
          <Tooltip title="Nome que a IA usa internamente para pedir este arquivo">
            <Box sx={{
              fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.6rem',
              color: C.text3, bgcolor: C.inset, border: `1px solid ${C.bdr}`,
              borderRadius: '4px', px: 0.7, py: 0.1, whiteSpace: 'nowrap',
            }}>
              {arquivo.slug}
            </Box>
          </Tooltip>
        </Stack>

        <TextField
          variant="standard"
          fullWidth
          value={arquivo.description || ''}
          onChange={e => onMudar({ ...arquivo, description: e.target.value })}
          placeholder="Quando enviar? ex.: quando pedirem cardápio, preços, o que tem"
          inputProps={{ maxLength: 200 }}
          sx={{
            '& .MuiInput-input': {
              fontFamily: SYNE, fontSize: '0.7rem', color: C.text2, padding: '1px 0',
            },
            '& .MuiInput-underline:before': { borderBottom: 'none' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottom: `1px solid ${C.bdr2}`,
            },
          }}
        />
        {!(arquivo.description || '').trim() && (
          <Typography sx={{
            fontFamily: SYNE, fontSize: '0.62rem', color: C.amberD, mt: 0.3,
          }}>
            Sem esta descrição a IA não tem como saber quando enviar o arquivo.
          </Typography>
        )}
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.2} sx={{ flexShrink: 0 }}>
        <Tooltip title="Remover da biblioteca">
          <span>
            <IconButton size="small" disabled={salvando}
              aria-label={`Remover ${arquivo.title}`}
              onClick={onRemover}
              sx={{ color: C.text3, '&:hover': { color: C.red } }}>
              <FiTrash2 size={12} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={desligado
          ? 'Desligado: a IA não pode enviar este arquivo'
          : 'Ligado: a IA pode sugerir o envio'}>
          <Switch
            size="small"
            checked={!desligado}
            onChange={e => onMudar({ ...arquivo, active: e.target.checked })}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: C.green },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.green },
            }}
          />
        </Tooltip>
      </Stack>
    </Stack>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────

export default function AutomacaoIA({ onToast }) {
  // Provider selecionado (default: CLAUDE — único ativo dia 1).
  // IDs em UPPERCASE batem direto com o enum AIProvider do backend.
  const [provider, setProvider] = useState('CLAUDE');

  // Snapshot do modelo em uso, vindo do backend (pin exato tipo
  // "claude-3-5-sonnet-20241022" quando já houve chamada real, ou
  // o alias configurado no settings quando é conexão fresca).
  const [activeModel, setActiveModel] = useState(null);

  // Estado da chave BYOK
  const [apiKey, setApiKey] = useState('');
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [keyValidated, setKeyValidated] = useState(false);
  const [keyError, setKeyError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [savedKeyMask, setSavedKeyMask] = useState(''); // vindo do backend após save/fetch

  // Master toggle "Ativar IA no chat" — controla o PILOTO AUTOMÁTICO
  // (`ai_enabled` no backend): com ele ligado, a IA responde sozinha as
  // mensagens que chegam no WhatsApp após a janela de debounce.
  //
  // NÃO confundir com `enabled` do backend, que significa "existe chave
  // BYOK válida" e é gerenciado implicitamente por POST/DELETE
  // /ai/config/key. Por isso o toggle lê/escreve `ai_enabled` e usa
  // `has_key`/`enabled` só como pré-requisito pra habilitar o clique.
  //
  // Persiste na hora (PUT /ai/config), sem depender do botão "Salvar
  // diretrizes": ligar/desligar resposta automática é uma ação de
  // consequência imediata — deixar pendente num botão de salvar lá
  // embaixo é como o cliente acaba achando que desligou e não desligou.
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [savingMaster, setSavingMaster] = useState(false);
  // Janela de debounce vinda do backend — a copy usa esse número em vez
  // de hardcodar "40s", então mudar AI_AUTO_REPLY_DEBOUNCE_SECONDS no
  // .env não deixa a UI mentindo.
  const [debounceSeconds, setDebounceSeconds] = useState(40);

  // Form de tuning — populado via GET /ai/config no mount, persistido
  // via PUT /ai/config em saveTune. Tone chega uppercase do backend
  // (FRIENDLY/FORMAL/…); guardo lowercase no state pra o Select MUI
  // funcionar direto — converto no envio.
  const [assistantName, setAssistantName] = useState('');
  const [tone, setTone] = useState('friendly');
  const [segment, setSegment] = useState('');
  const [restrictions, setRestrictions] = useState('');
  const [savingTune, setSavingTune] = useState(false);

  // Blocos de contexto. `_key` é só pro React — bloco recém-criado ainda
  // não tem `id` do banco, e usar o índice como key faz o cursor pular
  // de campo quando o cliente reordena enquanto digita.
  const [blocos, setBlocos] = useState([]);
  const [limites, setLimites] = useState({ limit_chars: 28000, max_blocks: 10 });
  const [menuModelo, setMenuModelo] = useState(null);
  // Página aberta na janela (índice na lista) e se o cursor vai pro título
  // (página nova em branco) em vez do texto.
  const [aberta, setAberta] = useState(null);
  const [focarTitulo, setFocarTitulo] = useState(false);
  // Arrastar pra reordenar: de onde saiu e sobre qual card está.
  const [arrastando, setArrastando] = useState(null);
  const [alvoDoArraste, setAlvoDoArraste] = useState(null);
  // O que está gravado no banco, pra marcar o que mudou e ainda não foi
  // salvo. Com o texto escondido atrás de uma janela e o "Salvar
  // diretrizes" lá embaixo, sem essa marca o cliente fecha a página
  // achando que já valeu.
  const [salvoBlocos, setSalvoBlocos] = useState([]);
  const [salvoAjustes, setSalvoAjustes] = useState(null);
  const tema = useTheme();
  const telaPequena = useMediaQuery(tema.breakpoints.down('sm'));
  // Biblioteca de arquivos que a IA pode pedir pra enviar. Diferente dos
  // blocos, estes persistem na hora (upload/apagar são ações imediatas) —
  // deixar um arquivo pendente num "Salvar" lá embaixo é como o cliente
  // acaba achando que subiu e não subiu.
  const [arquivos, setArquivos] = useState([]);
  const [subindo, setSubindo] = useState(false);
  const [importandoPdf, setImportandoPdf] = useState(false);

  // Stats reais dos últimos 30d — alimenta os 3 KPIs (Sugestões/Aceitação/Latência).
  const [stats, setStats] = useState(null); // { total_suggestions, used_pct, ... }
  const [loadingConfig, setLoadingConfig] = useState(true);

  const currentProvider = useMemo(() => PROVIDERS.find(p => p.id === provider) || PROVIDERS[0], [provider]);

  // ─── Fetch inicial: config + stats em paralelo ─────────────────────────
  const fetchAll = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const [cfgRes, statsRes, assetsRes] = await Promise.all([
        api.get('/ai/config'),
        api.get('/ai/suggest-reply/stats').catch(() => ({ data: null })),
        api.get('/ai/assets').catch(() => ({ data: [] })),
      ]);
      setArquivos(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      const cfg = cfgRes.data || {};
      if (cfg.provider) setProvider(cfg.provider);
      if (cfg.assistant_name) setAssistantName(cfg.assistant_name);
      if (cfg.tone) setTone(String(cfg.tone).toLowerCase());
      if (cfg.segment) setSegment(cfg.segment);
      if (cfg.restrictions) setRestrictions(cfg.restrictions);
      // A migração copia o `about_company` antigo pro primeiro bloco, então
      // normalmente já vem preenchido. O fallback cobre o intervalo entre o
      // deploy do backend e a migration ter rodado: em vez de a tela
      // aparecer vazia e o cliente achar que perdeu o texto, mostramos o
      // campo legado como primeira página.
      const vindos = Array.isArray(cfg.context_blocks) ? cfg.context_blocks : [];
      setSalvoBlocos(vindos);
      setSalvoAjustes({
        assistantName: cfg.assistant_name || '',
        tone: cfg.tone ? String(cfg.tone).toLowerCase() : 'friendly',
        segment: cfg.segment || '',
        restrictions: cfg.restrictions || '',
      });
      if (vindos.length) {
        setBlocos(vindos.map(b => ({ ...b, _key: b.id })));
      } else if (cfg.about_company) {
        setBlocos([{
          _key: 'legado', id: null, title: 'Sobre a empresa',
          content: cfg.about_company, active: true,
        }]);
      } else {
        setBlocos([]);
      }
      if (cfg.context_usage) setLimites(cfg.context_usage);
      setKeyValidated(!!cfg.has_key);
      setSavedKeyMask(cfg.key_mask || '');
      setActiveModel(cfg.model || null);
      setMasterEnabled(!!cfg.ai_enabled);
      if (cfg.auto_reply_debounce_seconds) setDebounceSeconds(cfg.auto_reply_debounce_seconds);
      setStats(statsRes.data || null);
    } catch (err) {
      // 401 = deslogado (interceptor futuro vai redirecionar). Outros
      // erros: mostra toast mas não bloqueia — o form fica vazio,
      // usuário pode tentar de novo.
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Não deu pra carregar sua config de IA.') });
    } finally {
      setLoadingConfig(false);
    }
  }, [onToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handler: quando muda provider, limpa estado de chave (chave de um provider
  // não vale pra outro; forçar re-conexão evita usuário achar que "está pronto"
  // depois de trocar provider sem validar de novo).
  const selectProvider = (id) => {
    if (id === provider) return;
    setProvider(id);
    setApiKey('');
    setKeyValidated(false);
    setKeyError(null);
    setMasterEnabled(false);
    setSavedKeyMask('');
    setActiveModel(null);
  };

  // Handler: valida chave contra o provider e salva cifrada.
  // POST /ai/config/key faz ping ao Anthropic, cifra com Fernet, persiste.
  const validateKey = async () => {
    const trimmed = apiKey.trim();
    if (trimmed.length < 8) {
      setKeyError('Chave muito curta.');
      return;
    }
    setKeyError(null);
    setValidating(true);
    try {
      const res = await api.post('/ai/config/key', { provider, api_key: trimmed });
      const data = res.data || {};
      setKeyValidated(true);
      setSavedKeyMask(data.key_mask || (trimmed.slice(0, 10) + '•'.repeat(28)));
      setActiveModel(data.model || null);
      setApiKey('');
      // NÃO derivamos o toggle de `data.enabled` (que só diz "chave
      // válida"). Conectar chave não pode ligar resposta automática
      // sozinha — isso é decisão explícita do cliente. O refetch traz o
      // `ai_enabled` real, que o backend preserva entre trocas de chave.
      fetchAll();
      onToast && onToast({ kind: 'success', msg: `Chave validada — modelo ${data.model || 'em uso'}.` });
    } catch (err) {
      const status = err?.response?.status;
      const detail = parseErrorDetail(err, '');
      if (status === 401) {
        setKeyError(detail || 'Chave inválida ou sem permissão pro modelo. Verifique no console.anthropic.com.');
      } else if (status === 429) {
        setKeyError('Muitas tentativas de validação em pouco tempo. Aguarde 1 minuto.');
      } else if (status === 503) {
        setKeyError(detail || 'IA não está configurada no servidor (falta ENCRYPTION_KEY).');
      } else {
        setKeyError(detail || `Erro ao validar (${status || 'sem status'}).`);
      }
    } finally {
      setValidating(false);
    }
  };

  const replaceKey = async () => {
    try {
      await api.delete('/ai/config/key');
    } catch (err) {
      // Se falhar, ainda limpa a UI — próxima carga corrige.
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Erro ao remover chave.') });
    }
    setKeyValidated(false);
    setSavedKeyMask('');
    setApiKey('');
    setKeyError(null);
    setMasterEnabled(false);
    setActiveModel(null);
  };

  // Liga/desliga o piloto automático e persiste na hora.
  // Otimista: pinta o toggle antes da resposta (a UI trava enquanto salva)
  // e REVERTE se o backend recusar — o pior cenário aqui é o cliente achar
  // que desligou a IA e ela continuar respondendo os clientes dele.
  const toggleMaster = async () => {
    if (!keyValidated || savingMaster) return;
    const next = !masterEnabled;
    setMasterEnabled(next);
    setSavingMaster(true);
    try {
      const res = await api.put('/ai/config', { ai_enabled: next });
      setMasterEnabled(!!res.data?.ai_enabled);
      onToast && onToast({
        kind: 'success',
        msg: next
          ? `IA ativada — vai responder automaticamente após ${debounceSeconds}s.`
          : 'IA desativada — atendimento volta a ser manual.',
      });
    } catch (err) {
      setMasterEnabled(!next);  // reverte
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Erro ao alterar o status da IA.') });
    } finally {
      setSavingMaster(false);
    }
  };

  const saveTune = async () => {
    setSavingTune(true);
    // O que está indo pro banco — vira a referência do "alterado" se der
    // certo (o cliente pode continuar digitando enquanto salva).
    const ajustesEnviados = { assistantName, tone, segment, restrictions };
    try {
      const payload = {
        assistant_name: assistantName || 'Assistente',
        tone: (tone || 'friendly').toUpperCase(),   // backend enum uppercase
        segment: segment || null,
        restrictions: restrictions || null,
        // Lista completa: o backend trata como substituição. `_key` é
        // estado de tela e não vai no wire; `id: null` marca bloco novo.
        context_blocks: blocos.map(b => ({
          id: b.id || null,
          title: (b.title || '').trim(),
          content: b.content || '',
          active: !!b.active,
        })),
      };
      const res = await api.put('/ai/config', payload);
      const cfg = res.data || {};
      if (cfg.tone) setTone(String(cfg.tone).toLowerCase());
      // Reidrata com o que o banco gravou: os blocos novos voltam com id,
      // e sem isso o próximo "Salvar" os recriaria em vez de atualizar.
      if (Array.isArray(cfg.context_blocks)) {
        setBlocos(cfg.context_blocks.map(b => ({ ...b, _key: b.id })));
        setSalvoBlocos(cfg.context_blocks);
      }
      setSalvoAjustes({ ...ajustesEnviados, tone: cfg.tone ? String(cfg.tone).toLowerCase() : ajustesEnviados.tone });
      if (cfg.context_usage) setLimites(cfg.context_usage);
      onToast && onToast({ kind: 'success', msg: 'Diretrizes salvas.' });
    } catch (err) {
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Erro ao salvar diretrizes.') });
    } finally {
      setSavingTune(false);
    }
  };

  const canValidate = apiKey.trim().length >= 8 && !validating;

  // ─── Operações sobre as páginas de contexto ────────────────────────────
  const mudarBloco = (i, novo) =>
    setBlocos(bs => bs.map((b, k) => (k === i ? { ...novo, _key: b._key } : b)));

  const removerBloco = (i) => {
    setAberta(null);
    setBlocos(bs => bs.filter((_, k) => k !== i));
  };

  // Tira a página de `de` e põe em `para` (arrastar, ou as setas da
  // janela). A janela aberta acompanha a página que mudou de lugar.
  const moverBloco = (de, para) => {
    if (de === para || para < 0 || para >= blocos.length) return;
    setBlocos(bs => {
      const copia = [...bs];
      const [pagina] = copia.splice(de, 1);
      copia.splice(para, 0, pagina);
      return copia;
    });
    setAberta(a => (a === de ? para : a));
  };

  const abrirPagina = (i, titulo = false) => {
    setFocarTitulo(titulo);
    setAberta(i);
  };

  // Página nova já abre na janela: com modelo, o cursor vai pro texto;
  // em branco, pro nome.
  const adicionarBloco = (modelo) => {
    setMenuModelo(null);
    const emBranco = modelo.titulo === 'Em branco';
    abrirPagina(blocos.length, emBranco);
    setBlocos(bs => [...bs, {
      _key: `novo-${Date.now()}-${bs.length}`,
      id: null,
      title: emBranco ? '' : modelo.titulo,
      content: '',
      active: true,
    }]);
  };

  // Página com mudança ainda não salva: nova, editada, ligada/desligada ou
  // fora do lugar em que está no banco.
  const salvoPorId = useMemo(
    () => Object.fromEntries(salvoBlocos.map((b, i) => [b.id, { ...b, posicao: i }])),
    [salvoBlocos],
  );
  const paginaAlterada = (b, i) => {
    const s = b.id && salvoPorId[b.id];
    return !s || s.posicao !== i || s.title !== b.title
      || (s.content || '') !== (b.content || '') || !!s.active !== !!b.active;
  };
  const diretrizesAlteradas = !!salvoAjustes && (
    blocos.length !== salvoBlocos.length
    || blocos.some(paginaAlterada)
    || salvoAjustes.assistantName !== assistantName
    || salvoAjustes.tone !== tone
    || salvoAjustes.segment !== segment
    || salvoAjustes.restrictions !== restrictions
  );

  // Mesma conta do backend (ai_contexto.total_usado): só bloco LIGADO
  // pesa, e as restrições entram no mesmo orçamento porque é tudo um
  // prompt só. Calculado aqui pra a barra reagir enquanto o cliente
  // digita; o número que manda de verdade é o do backend, no save.
  const contextoUsado = useMemo(() => (
    blocos.reduce((soma, b) => soma + (b.active ? (b.content || '').trim().length : 0), 0)
    + (restrictions || '').trim().length
  ), [blocos, restrictions]);

  const contextoEstourou = contextoUsado > (limites.limit_chars || 28000);
  const semVagaDeBloco = blocos.length >= (limites.max_blocks || 10);

  // ─── Importar PDF como página de contexto ──────────────────────────────
  // O backend só EXTRAI; nada é gravado até o "Salvar diretrizes". É o que
  // dá ao cliente a chance de corrigir o que a extração entendeu errado
  // antes de o texto entrar no prompt.
  const importarPdf = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';           // permite reimportar o mesmo arquivo
    if (!arquivo) return;
    setImportandoPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', arquivo);
      const { data } = await api.post('/ai/context/extract', fd);
      // Abre a página importada: é ali que o cliente confere o que a
      // extração entendeu antes de salvar.
      abrirPagina(blocos.length);
      setBlocos(bs => [...bs, {
        _key: `pdf-${Date.now()}`,
        id: null,
        title: (data.source_name || 'Documento').replace(/\.pdf$/i, '').slice(0, 80),
        content: data.text || '',
        active: true,
      }]);
      onToast && onToast({
        kind: data.warning ? 'error' : 'success',
        msg: data.warning
          || `Texto importado (${(data.chars || 0).toLocaleString('pt-BR')} caracteres). `
             + 'Confira e salve.',
      });
    } catch (err) {
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Não consegui ler o arquivo.') });
    } finally {
      setImportandoPdf(false);
    }
  };

  // ─── Biblioteca de arquivos ────────────────────────────────────────────
  const subirArquivo = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    setSubindo(true);
    try {
      const fd = new FormData();
      fd.append('file', arquivo);
      // Nome do arquivo como título inicial; o cliente renomeia na lista.
      fd.append('title', arquivo.name.replace(/\.[^.]+$/, '').slice(0, 80) || 'Arquivo');
      fd.append('description', '');
      const { data } = await api.post('/ai/assets', fd);
      setArquivos(as => [...as, data]);
      onToast && onToast({
        kind: 'success',
        msg: 'Arquivo guardado. Escreva quando a IA deve enviá-lo.',
      });
    } catch (err) {
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Não consegui guardar o arquivo.') });
    } finally {
      setSubindo(false);
    }
  };

  const mudarArquivo = async (novo) => {
    // Otimista: a lista reflete na hora e o PATCH confirma. Reverter num
    // erro de rede é pior que deixar o campo como o cliente digitou — ele
    // reedita; o toast avisa.
    setArquivos(as => as.map(a => (a.id === novo.id ? novo : a)));
    try {
      await api.patch(`/ai/assets/${novo.id}`, {
        title: novo.title, description: novo.description, active: novo.active,
      });
    } catch (err) {
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Não consegui salvar a alteração.') });
    }
  };

  const removerArquivo = async (id) => {
    const alvo = arquivos.find(a => a.id === id);
    if (alvo && !window.confirm(`Remover "${alvo.title}" da biblioteca?`)) return;
    try {
      await api.delete(`/ai/assets/${id}`);
      setArquivos(as => as.filter(a => a.id !== id));
    } catch (err) {
      onToast && onToast({ kind: 'error', msg: parseErrorDetail(err, 'Não consegui remover o arquivo.') });
    }
  };

  return (
    <Box>
      {/* ═══ SECTION 1: KPIs ═══════════════════════════════════════════ */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 1.5, mb: 3,
      }}>
        {/* KPI 1: Provedor Ativo — quebra o padrão dos outros 3 (mostra
            logo+nome em vez de número). Sinaliza que essa métrica é sobre
            identidade de conexão, não volume. */}
        <KpiCard label="Provedor ativo" gradient={`linear-gradient(90deg, ${C.purple}, ${C.blue})`}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '7px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: OUTFIT, fontWeight: 900, fontSize: '0.95rem',
              background: currentProvider.logoBg, color: currentProvider.logoColor,
            }}>
              {currentProvider.logoText}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontFamily: SYNE, fontSize: '0.85rem', fontWeight: 700,
                color: C.text, lineHeight: 1.2,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {currentProvider.name}
              </Typography>
              <Typography sx={{
                fontFamily: OUTFIT, fontSize: '0.62rem', color: C.text3,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {activeModel || currentProvider.defaultModel}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ mt: 1.2 }}>
            <Chip
              size="small"
              label={keyValidated ? '● Conectado' : '○ Aguardando chave'}
              sx={{
                height: 20, borderRadius: '20px',
                bgcolor: keyValidated ? C.greenBg : C.inset,
                color: keyValidated ? C.greenD : C.text3,
                border: `1px solid ${keyValidated ? C.greenB : C.bdr}`,
                fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>
        </KpiCard>

        {/* KPI 2: Respostas geradas (últimos 30d) — vem de GET /ai/suggest-reply/stats */}
        <KpiCard label="Respostas geradas" gradient={`linear-gradient(90deg, ${C.green}, #00e088)`}>
          <Typography sx={{
            fontFamily: OUTFIT, fontSize: '1.9rem', fontWeight: 800,
            letterSpacing: '-1px', lineHeight: 1, color: C.text, mb: 0.5,
          }}>
            {stats?.total_suggestions != null
              ? stats.total_suggestions.toLocaleString('pt-BR')
              : '—'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <Chip
              size="small"
              icon={<FiZap size={9} />}
              label={
                stats?.total_suggestions > 0
                  ? `${stats.tokens_last_30d?.toLocaleString('pt-BR') || 0} tokens`
                  : 'Sem histórico'
              }
              sx={{
                height: 20, borderRadius: '20px',
                bgcolor: stats?.total_suggestions > 0 ? C.greenBg : C.inset,
                color: stats?.total_suggestions > 0 ? C.greenD : C.text3,
                border: `1px solid ${stats?.total_suggestions > 0 ? C.greenB : C.bdr}`,
                fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
                letterSpacing: '0.04em',
                '& .MuiChip-label': { px: 0.7 },
              }}
            />
          </Stack>
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.62rem', color: C.text3, mt: 0.6 }}>
            Últimos 30 dias
          </Typography>
        </KpiCard>

        {/* KPI 3: Taxa de Aceitação — usadas + editadas / total. */}
        <KpiCard label="Taxa de aceitação" gradient={`linear-gradient(90deg, ${C.blue}, #60a5fa)`}>
          <Typography sx={{
            fontFamily: OUTFIT, fontSize: '1.9rem', fontWeight: 800,
            letterSpacing: '-1px', lineHeight: 1, color: C.text, mb: 0.5,
          }}>
            {stats?.total_suggestions > 0
              ? <>{Math.round(((stats.used_pct || 0) + (stats.edited_pct || 0)))}<Box component="span" sx={{ fontSize: '0.95rem', color: C.text3, fontWeight: 600 }}>%</Box></>
              : '—'}
          </Typography>
          <Chip
            size="small"
            icon={<FiBarChart2 size={9} />}
            label="Aceitas ou editadas"
            sx={{
              height: 20, borderRadius: '20px',
              bgcolor: C.blueBg, color: C.blueD, border: `1px solid ${C.blueB}`,
              fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
              letterSpacing: '0.04em',
              '& .MuiChip-label': { px: 0.7 },
            }}
          />
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.62rem', color: C.text3, mt: 0.6 }}>
            Por agentes humanos
          </Typography>
        </KpiCard>

        {/* KPI 4: Latência Média — avg dos AISuggestionLog do período. */}
        <KpiCard label="Latência média" gradient={`linear-gradient(90deg, ${C.teal}, #38bdf8)`}>
          <Typography sx={{
            fontFamily: OUTFIT, fontSize: '1.9rem', fontWeight: 800,
            letterSpacing: '-1px', lineHeight: 1, color: C.text, mb: 0.5,
          }}>
            {stats?.avg_latency_ms != null
              ? <>{(stats.avg_latency_ms / 1000).toFixed(1)}<Box component="span" sx={{ fontSize: '0.95rem', color: C.text3, fontWeight: 600, ml: 0.2 }}>s</Box></>
              : '—'}
          </Typography>
          <Chip
            size="small"
            icon={<FiClock size={9} />}
            label="API do provedor"
            sx={{
              height: 20, borderRadius: '20px',
              bgcolor: C.purpBg, color: C.purple, border: `1px solid ${C.purpB}`,
              fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
              letterSpacing: '0.04em',
              '& .MuiChip-label': { px: 0.7 },
            }}
          />
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.62rem', color: C.text3, mt: 0.6 }}>
            Tempo real do provider
          </Typography>
        </KpiCard>
      </Box>

      {/* ═══ SECTION 2: BYOK Provider Config ═════════════════════════════ */}
      <Box sx={{
        bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
        overflow: 'hidden', boxShadow: C.sombra, mb: 2,
      }}>
        <SectionHead
          icon={<FiCpu size={17} />}
          iconBg={C.purpBg} iconBorder={C.purpB} iconColor={C.purple}
          title="Provedor de inteligência artificial"
          sub="A Cetto orquestra a inteligência usando a chave da sua empresa. Custo direto no seu provedor — sem intermediários."
          right={
            <Chip
              size="small"
              icon={<FiLock size={10} />}
              label="Chave nunca fica exposta na UI"
              sx={{
                height: 22, borderRadius: '6px',
                bgcolor: C.amberBg, color: C.amberD, border: `1px solid ${C.amberB}`,
                fontFamily: SYNE, fontSize: '0.62rem', fontWeight: 600,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          }
        />

        <Box sx={{ p: 2.5 }}>
          {/* Provider grid */}
          <TuneLabel>Selecione o provedor</TuneLabel>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 1.5, mb: 2.5,
          }}>
            {PROVIDERS.map(p => (
              <ProviderCard
                key={p.id} p={p}
                selected={p.id === provider && p.available}
                onSelect={selectProvider}
                activeModel={activeModel}
              />
            ))}
          </Box>

          {/* Chave: 2 estados exclusivos */}
          <TuneLabel>Chave de acesso</TuneLabel>

          {!keyValidated ? (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems="stretch">
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: C.surf,
                  border: `1px solid ${keyError ? C.redB : C.bdr2}`,
                  borderRadius: '6px', px: 1.4, minHeight: 42,
                  transition: 'border-color .2s, background .2s',
                  '&:focus-within': { borderColor: keyError ? C.redB : C.greenB, bgcolor: C.card },
                }}>
                  <FiKey size={14} color={C.text3} />
                  <TextField
                    variant="standard"
                    fullWidth
                    type={apiKeyVisible ? 'text' : 'password'}
                    value={apiKey}
                    onChange={e => { setApiKey(e.target.value); setKeyError(null); }}
                    placeholder={currentProvider.placeholder}
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontFamily: OUTFIT, fontSize: '0.82rem', color: C.text },
                    }}
                  />
                  <Box
                    onClick={() => setApiKeyVisible(v => !v)}
                    sx={{
                      cursor: 'pointer', color: C.text3, display: 'flex',
                      p: 0.5, borderRadius: '4px',
                      '&:hover': { color: C.text2, bgcolor: C.inset },
                    }}
                  >
                    {apiKeyVisible ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                  </Box>
                </Box>
                <Button
                  onClick={validateKey}
                  disabled={!canValidate}
                  startIcon={validating ? <CircularProgress size={12} color="inherit" /> : null}
                  sx={{
                    bgcolor: C.green, color: C.sobreCor, px: 2.4, borderRadius: '6px',
                    fontFamily: SYNE, fontWeight: 700, fontSize: '0.75rem',
                    textTransform: 'none', boxShadow: `0 2px 6px ${C.greenGlow}`,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: C.greenHover },
                    '&.Mui-disabled': { bgcolor: C.inset, color: C.text3 },
                  }}
                >
                  {validating ? 'Validando…' : 'Validar e salvar chave'}
                </Button>
              </Stack>

              {keyError && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{
                  mt: 1.2, p: '10px 13px', bgcolor: C.redBg, border: `1px solid ${C.redB}`,
                  borderRadius: '6px',
                }}>
                  <FiAlertCircle size={14} color={C.red} style={{ flexShrink: 0, marginTop: 2 }} />
                  <Typography sx={{ fontFamily: SYNE, fontSize: '0.72rem', color: C.redD, lineHeight: 1.55 }}>
                    {keyError}
                  </Typography>
                </Stack>
              )}
            </>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{
              bgcolor: C.greenBg, border: `1px solid ${C.greenB}`, borderRadius: '6px',
              p: '12px 14px',
            }}>
              <FiLock size={16} color={C.greenD} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', fontWeight: 700, color: C.greenD }}>
                  Chave criptografada e válida
                </Typography>
                <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.66rem', color: C.greenD, opacity: 0.75, wordBreak: 'break-all' }}>
                  {savedKeyMask}
                </Typography>
              </Box>
              <Button
                onClick={replaceKey}
                startIcon={<FiRefreshCw size={11} />}
                sx={{
                  fontFamily: SYNE, fontSize: '0.7rem', fontWeight: 600,
                  color: C.text2, bgcolor: C.card, border: `1px solid ${C.bdr2}`,
                  borderRadius: '6px', px: 1.4, py: 0.5, textTransform: 'none',
                  '&:hover': { borderColor: C.redB, color: C.redD, bgcolor: C.card },
                }}
              >
                Substituir chave
              </Button>
            </Stack>
          )}

          {/* AI Master Toggle */}
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{
            mt: 2, p: '14px 16px', borderRadius: '6px',
            bgcolor: masterEnabled ? C.greenBg : C.surf,
            border: `1px solid ${masterEnabled ? C.greenB : C.bdr}`,
            transition: 'all .2s',
          }}>
            <Box>
              <Typography sx={{ fontFamily: SYNE, fontSize: '0.85rem', fontWeight: 700, color: C.text }}>
                Ativar IA no chat
              </Typography>
              <Typography sx={{
                fontFamily: SYNE, fontSize: '0.72rem',
                color: keyValidated ? C.text3 : C.amberD, mt: 0.3,
              }}>
                {keyValidated
                  ? `Quando ativo, a IA responde automaticamente às mensagens recebidas no WhatsApp (após janela de ${debounceSeconds}s). Quando desativado, o atendimento é mantido manual.`
                  : 'Configure e valide uma chave acima pra ativar.'}
              </Typography>
              {/* O botão "Sugerir com IA" não depende deste toggle — some
                  gente desliga o piloto automático achando que perde o
                  copiloto no chat também. A linha existe pra impedir isso. */}
              {keyValidated && (
                <Typography sx={{
                  fontFamily: SYNE, fontSize: '0.68rem', color: C.text3,
                  mt: 0.5, fontStyle: 'italic',
                }}>
                  O botão "Sugerir com IA" continua disponível no chat nos dois modos.
                </Typography>
              )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box
                onClick={toggleMaster}
                sx={{
                  width: 42, height: 24, borderRadius: '12px',
                  bgcolor: masterEnabled ? C.green : C.inset,
                  border: `1.5px solid ${masterEnabled ? C.green : C.bdr2}`,
                  position: 'relative',
                  cursor: !keyValidated ? 'not-allowed' : (savingMaster ? 'wait' : 'pointer'),
                  opacity: keyValidated ? (savingMaster ? 0.6 : 1) : 0.45,
                  transition: 'all .2s',
                }}
              >
                <Box sx={{
                  position: 'absolute', top: 3, left: masterEnabled ? 22 : 3,
                  width: 14, height: 14, borderRadius: '50%', bgcolor: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left .18s',
                }} />
              </Box>
              <Typography sx={{
                fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700,
                color: masterEnabled ? C.greenD : C.text2, minWidth: 46,
              }}>
                {masterEnabled ? 'Ativo' : 'Inativo'}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* ═══ SECTION 3: Diretrizes do Copiloto ══════════════════════════ */}
      <Box sx={{
        bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
        overflow: 'hidden', boxShadow: C.sombra, mb: 3,
      }}>
        <SectionHead
          icon={<FiTarget size={17} />}
          iconBg={C.greenBg} iconBorder={C.greenB} iconColor={C.greenD}
          title="Diretrizes do copiloto"
          sub="Configure o comportamento, tom de voz e contexto que a IA usará para sugerir respostas aos seus agentes."
        />

        <Box sx={{ p: 2.5 }}>
          {/* Row 1: 3 campos lado a lado */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 1.8, mb: 2.2,
          }}>
            <Box>
              <TuneLabel>Nome do assistente</TuneLabel>
              <TextField
                fullWidth size="small"
                value={assistantName}
                onChange={e => setAssistantName(e.target.value)}
                placeholder="Ex: Ana — Copiloto"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: SYNE, fontSize: '0.8rem', bgcolor: C.surf, borderRadius: '6px',
                    '& fieldset': { borderColor: C.bdr2 },
                    '&:hover fieldset': { borderColor: C.bdr2 },
                    '&.Mui-focused fieldset': { borderColor: C.greenB },
                    '&.Mui-focused': { bgcolor: C.card },
                  },
                }}
              />
            </Box>
            <Box>
              <TuneLabel>Tom de voz</TuneLabel>
              <Select
                fullWidth size="small"
                value={tone}
                onChange={e => setTone(e.target.value)}
                sx={{
                  fontFamily: SYNE, fontSize: '0.8rem', bgcolor: C.surf, borderRadius: '6px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.bdr2 },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.greenB },
                }}
              >
                <MenuItem value="friendly" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Amigável e próximo</MenuItem>
                <MenuItem value="formal" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Formal / jurídico</MenuItem>
                <MenuItem value="technical" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Técnico e direto</MenuItem>
                <MenuItem value="consultative" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Consultivo e estratégico</MenuItem>
              </Select>
            </Box>
            <Box>
              <TuneLabel>Segmento do negócio</TuneLabel>
              <Select
                fullWidth size="small"
                value={segment}
                onChange={e => setSegment(e.target.value)}
                sx={{
                  fontFamily: SYNE, fontSize: '0.8rem', bgcolor: C.surf, borderRadius: '6px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.bdr2 },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.greenB },
                }}
              >
                <MenuItem value="advocacia" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Advocacia</MenuItem>
                <MenuItem value="saas" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>SaaS / tecnologia</MenuItem>
                <MenuItem value="imobiliaria" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Imobiliária</MenuItem>
                <MenuItem value="clinica" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Clínica / saúde</MenuItem>
                <MenuItem value="ecommerce" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>E-commerce</MenuItem>
                <MenuItem value="educacao" sx={{ fontFamily: SYNE, fontSize: '0.8rem' }}>Escola / educação</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Páginas de contexto — substituíram o textarea único "Sobre a
              empresa". O cliente monta o próprio prompt em seções
              nomeadas e vê quanto cada uma custa. */}
          <Box sx={{ mb: 2.2 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5} flexWrap="wrap">
              <TuneLabel hint="o que a IA sabe sobre o seu negócio, na ordem em que ela lê">
                Páginas de contexto
              </TuneLabel>
              {blocos.length > 0 && (
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.62rem', color: C.text3, mb: 0.6 }}>
                  {/* Arrastar não existe no toque: lá a ordem muda pelas setas da janela. */}
                  <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                    Clique numa página para abrir · arraste para mudar a ordem
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                    Toque numa página para abrir
                  </Box>
                </Typography>
              )}
            </Stack>

            <MedidorDeContexto
              usado={contextoUsado}
              limite={limites.limit_chars || 28000}
              maxBlocos={limites.max_blocks || 10}
              blocosUsados={blocos.length}
            />

            {blocos.length === 0 && (
              <Box sx={{
                border: `1px dashed ${C.bdr2}`, borderRadius: '8px',
                p: 2.4, textAlign: 'center', mb: 1.4,
              }}>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.78rem', color: C.text2, mb: 0.4 }}>
                  Nenhuma página de contexto ainda.
                </Typography>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.68rem', color: C.text3, lineHeight: 1.5 }}>
                  Comece pela descrição da empresa. Depois acrescente uma página
                  para o comportamento e outra para quebra de objeções.
                </Typography>
              </Box>
            )}

            {/* Três por fileira no computador; o mínimo de 220 px faz a
                grade cair pra duas e uma coluna sozinha. */}
            <Box sx={{
              display: 'grid', gap: 1.5,
              gridTemplateColumns: 'repeat(auto-fill, minmax(max(220px, calc((100% - 24px) / 3)), 1fr))',
            }}>
              {blocos.map((b, i) => (
                <CartaoDePagina
                  key={b._key}
                  bloco={b}
                  indice={i}
                  usado={contextoUsado}
                  alterada={paginaAlterada(b, i)}
                  arrastando={arrastando === i}
                  alvo={alvoDoArraste === i && arrastando !== null && arrastando !== i}
                  onAbrir={() => abrirPagina(i)}
                  onLigar={ligada => mudarBloco(i, { ...b, active: ligada })}
                  onArrastar={setArrastando}
                  onSobre={setAlvoDoArraste}
                  onSoltar={(para) => { if (arrastando !== null) moverBloco(arrastando, para); }}
                  onFimDoArraste={() => { setArrastando(null); setAlvoDoArraste(null); }}
                />
              ))}

              {semVagaDeBloco ? (
                <Stack alignItems="center" justifyContent="center" spacing={0.6} sx={{
                  minHeight: 150, p: 2, textAlign: 'center', color: C.text3,
                  border: `1px dashed ${C.bdr2}`, borderRadius: '8px',
                }}>
                  <FiMinus size={14} />
                  <Typography sx={{ fontFamily: SYNE, fontSize: '0.74rem', fontWeight: 700 }}>
                    Limite de {limites.max_blocks || 10} páginas atingido
                  </Typography>
                  <Typography sx={{ fontFamily: SYNE, fontSize: '0.64rem' }}>
                    Remova ou junte páginas para criar outra.
                  </Typography>
                </Stack>
              ) : (
                <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{
                  minHeight: 150, p: 2, textAlign: 'center',
                  border: `1px dashed ${C.greenB}`, borderRadius: '8px',
                  transition: 'background-color .15s, border-color .15s',
                  '&:hover': { bgcolor: C.greenBg, borderColor: C.green },
                }}>
                  <ButtonBase
                    onClick={e => setMenuModelo(e.currentTarget)}
                    aria-haspopup="menu"
                    sx={{
                      flexDirection: 'column', gap: 0.6, px: 1.5, py: 1, borderRadius: '8px',
                      '&.Mui-focusVisible': { boxShadow: `0 0 0 2px ${C.green}` },
                    }}
                  >
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '8px', bgcolor: C.card,
                      border: `1px solid ${C.greenB}`, color: C.greenD,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FiPlus size={14} />
                    </Box>
                    <Typography sx={{ fontFamily: SYNE, fontSize: '0.76rem', fontWeight: 700, color: C.greenD }}>
                      Adicionar página de contexto
                    </Typography>
                    <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.64rem', color: C.text3 }}>
                      {blocos.length} de {limites.max_blocks || 10} páginas
                    </Typography>
                  </ButtonBase>

                  {/* Importar PDF: a Cetto lê o texto e cria uma página com
                      ele. O arquivo em si NÃO é guardado aqui — pra a IA
                      poder ENVIAR o PDF, ele vai na biblioteca abaixo. São
                      usos diferentes do mesmo documento. */}
                  <Button
                    component="label"
                    disabled={importandoPdf}
                    startIcon={importandoPdf
                      ? <CircularProgress size={11} color="inherit" />
                      : <FiUpload size={11} />}
                    sx={{
                      fontFamily: SYNE, fontSize: '0.68rem', fontWeight: 700,
                      textTransform: 'none', color: C.blueD, bgcolor: C.card,
                      border: `1px dashed ${C.blueB}`, borderRadius: '6px',
                      px: 1, py: 0.3, minWidth: 0,
                      '&:hover': { bgcolor: C.blueBg, borderColor: C.blue },
                      '&.Mui-disabled': { color: C.text3, borderColor: C.bdr2 },
                    }}
                  >
                    {importandoPdf ? 'Lendo o PDF…' : 'Importar texto de um PDF'}
                    <input hidden type="file" accept="application/pdf,.pdf" onChange={importarPdf} />
                  </Button>
                </Stack>
              )}
            </Box>
            <Menu
              anchorEl={menuModelo}
              open={Boolean(menuModelo)}
              onClose={() => setMenuModelo(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              {MODELOS_DE_BLOCO.map(m => (
                <MenuItem key={m.titulo} onClick={() => adicionarBloco(m)}
                  sx={{ fontFamily: SYNE, fontSize: '0.78rem' }}>
                  {m.titulo}
                </MenuItem>
              ))}
            </Menu>

            {aberta !== null && blocos[aberta] && (
              <JanelaDaPagina
                chave={blocos[aberta]._key}
                bloco={blocos[aberta]}
                indice={aberta}
                total={blocos.length}
                usado={contextoUsado}
                placeholder={
                  (MODELOS_DE_BLOCO.find(m => m.titulo === blocos[aberta].title) || {}).placeholder
                  || 'Escreva aqui o que a IA precisa saber nesta página…'
                }
                focarTitulo={focarTitulo}
                telaCheia={telaPequena}
                onMudar={novo => mudarBloco(aberta, novo)}
                onMover={moverBloco}
                onIr={(i) => abrirPagina(i)}
                onRemover={removerBloco}
                onFechar={() => setAberta(null)}
              />
            )}
          </Box>

          {/* Biblioteca de arquivos — o que a IA pode ENVIAR.
              Fica entre o contexto e as restrições de propósito: é a última
              coisa que a IA pode FAZER, antes da lista do que ela NÃO pode. */}
          <Box sx={{ mb: 2.2 }}>
            <TuneLabel hint="o que a IA pode mandar pro cliente, e quando">
              Arquivos da IA
            </TuneLabel>

            {arquivos.map(a => (
              <ArquivoDaIA
                key={a.id}
                arquivo={a}
                salvando={subindo}
                onMudar={mudarArquivo}
                onRemover={() => removerArquivo(a.id)}
              />
            ))}

            {arquivos.length === 0 && (
              <Box sx={{
                border: `1px dashed ${C.bdr2}`, borderRadius: '8px',
                p: 2, mb: 1,
              }}>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.74rem', color: C.text2, mb: 0.4 }}>
                  Nenhum arquivo guardado.
                </Typography>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.66rem', color: C.text3, lineHeight: 1.5 }}>
                  Suba o cardápio, a tabela de preços ou fotos dos produtos. Quando
                  o cliente pedir, a IA sugere o envio e o agente confirma no chat.
                </Typography>
              </Box>
            )}

            <Button
              component="label"
              disabled={subindo}
              startIcon={subindo
                ? <CircularProgress size={11} color="inherit" />
                : <FiPaperclip size={12} />}
              sx={{
                fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700,
                textTransform: 'none', color: C.purple,
                border: `1px dashed ${C.purpB}`, borderRadius: '6px',
                px: 1.6, py: 0.7, width: '100%',
                '&:hover': { bgcolor: C.purpBg, borderColor: C.purple },
                '&.Mui-disabled': { color: C.text3, borderColor: C.bdr2 },
              }}
            >
              {subindo ? 'Guardando…' : 'Adicionar arquivo (PDF, JPG, PNG)'}
              <input hidden type="file"
                accept="application/pdf,.pdf,image/jpeg,image/png,image/webp"
                onChange={subirArquivo} />
            </Button>

            {/* A trava que o cliente precisa conhecer antes de confiar nisso. */}
            <Stack direction="row" alignItems="flex-start" spacing={0.9} sx={{
              mt: 1, p: '9px 12px', borderRadius: '6px',
              bgcolor: C.blueBg, border: `1px solid ${C.blueB}`,
            }}>
              <Box sx={{ color: C.blueD, mt: '1px' }}><FiLock size={11} /></Box>
              <Typography sx={{
                fontFamily: SYNE, fontSize: '0.66rem', color: C.blueD, lineHeight: 1.5,
              }}>
                A IA <strong>sugere</strong> o envio; quem confirma é o agente, no chat.
                Com o piloto automático ligado ela não manda arquivo sozinha —
                passa o atendimento para uma pessoa.
              </Typography>
            </Stack>
          </Box>

          {/* Restrições — SIGNATURE ELEMENT: danger zone amber */}
          <Box sx={{
            border: `1px solid ${C.amberB}`, borderRadius: '6px', overflow: 'hidden',
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
              px: 1.6, py: 1, bgcolor: C.amberBg, borderBottom: `1px solid ${C.amberB}`,
            }}>
              <Stack direction="row" alignItems="center" spacing={0.8}>
                <FiSlash size={13} color={C.amberD} />
                <Typography sx={{
                  fontFamily: SYNE, fontSize: '0.58rem', fontWeight: 700,
                  letterSpacing: '0.12em', textTransform: 'uppercase', color: C.amberD,
                }}>
                  Restrições obrigatórias
                </Typography>
              </Stack>
              <Chip
                size="small"
                label="NUNCA RESPONDA"
                sx={{
                  height: 20, borderRadius: '20px',
                  bgcolor: C.amber, color: C.sobreCor,
                  fontFamily: SYNE, fontSize: '0.56rem', fontWeight: 800,
                  letterSpacing: '0.06em',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Stack>
            <TextField
              fullWidth multiline minRows={4}
              value={restrictions}
              onChange={e => setRestrictions(e.target.value)}
              placeholder="Ex: Não prometer preços exatos. Não confirmar prazos de ativação. Não mencionar concorrentes…"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: SYNE, fontSize: '0.8rem',
                  bgcolor: C.amberBg, borderRadius: 0,
                  borderLeft: `3px solid ${C.amber}`,
                  lineHeight: 1.6,
                  '& fieldset': { border: 'none' },
                  '&.Mui-focused': { bgcolor: C.card, borderLeftColor: C.amberD },
                },
              }}
            />
          </Box>

          {/* Hint vermelho embaixo das restrições */}
          <Stack direction="row" alignItems="flex-start" spacing={0.9} sx={{
            mt: 1, p: '9px 12px', borderRadius: '6px',
            bgcolor: C.redBg, border: `1px solid ${C.redB}`,
          }}>
            <FiAlertCircle size={13} color={C.red} style={{ flexShrink: 0, marginTop: 1 }} />
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.7rem', color: C.redD, lineHeight: 1.55 }}>
              Estas restrições são injetadas no contexto do sistema e têm prioridade sobre qualquer instrução do agente. Revise regularmente pra garantir alinhamento com políticas comerciais atuais.
            </Typography>
          </Stack>
        </Box>

        {/* Footer: botão salvar */}
        <Stack direction="row" alignItems="center" spacing={1.2} sx={{
          px: 2.5, py: 1.6, borderTop: `1px solid ${C.bdr}`, bgcolor: C.surf,
        }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            bgcolor: contextoEstourou ? C.red : diretrizesAlteradas ? C.amber : C.green,
          }} />
          <Typography aria-live="polite" sx={{
            fontFamily: SYNE, fontSize: '0.72rem', flex: 1,
            color: contextoEstourou ? C.redD : diretrizesAlteradas ? C.amberD : C.text3,
          }}>
            {contextoEstourou
              ? 'O contexto passou do limite — ajuste antes de salvar.'
              : diretrizesAlteradas
                ? 'Há alterações que ainda não foram salvas.'
                : 'Contexto salvo é usado em toda sugestão gerada no chat.'}
          </Typography>
          <Button
            onClick={saveTune}
            // Travar aqui evita o vaivém de salvar, levar 400 e voltar pra
            // corrigir: o medidor já diz quanto falta cortar.
            disabled={savingTune || contextoEstourou}
            startIcon={savingTune ? <CircularProgress size={12} color="inherit" /> : <FiCheck size={12} />}
            sx={{
              bgcolor: C.green, color: C.sobreCor, px: 2.6, borderRadius: '6px',
              fontFamily: SYNE, fontWeight: 700, fontSize: '0.75rem',
              textTransform: 'none', boxShadow: `0 2px 6px ${C.greenGlow}`,
              '&:hover': { bgcolor: C.greenHover },
              '&.Mui-disabled': { bgcolor: C.inset, color: C.text3 },
            }}
          >
            {savingTune ? 'Salvando…' : 'Salvar diretrizes'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
